import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayArrowIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';

import { searchCompanies } from '../../api/companies';
import {
  approveSuggestion,
  getCandidateDocuments,
  getCompanySuggestions,
  getPipelineIntelligenceOverview,
  listPipelineIntelligenceCompanies,
  reopenSuggestion,
  rejectSuggestion,
  runPipelineIntelligencePreview,
} from '../../api/pipelineIntelligence';
import { CompanyListItem } from '../../types/company';
import {
  CompanySummary,
  DevelopmentModel,
  DifferenceType,
  PipelineIntelligenceOverview,
  PipelineSuggestion,
} from '../../types/pipelineIntelligence';
import { PipelineFieldEvidence } from './PipelineFieldEvidence';
import { PipelineBiologicalTarget } from './PipelineBiologicalTarget';

const DIFFERENCE_META: Record<
  DifferenceType,
  { label: string; color: 'success' | 'warning' | 'error' | 'info' | 'default' }
> = {
  missing_in_sozo: { label: 'Missing from SOZO', color: 'error' },
  source_conflict: { label: 'Sources disagree', color: 'warning' },
  stage_mismatch: { label: 'Stage mismatch', color: 'warning' },
  indication_mismatch: { label: 'Indication mismatch', color: 'warning' },
  needs_review: { label: 'Needs review', color: 'info' },
  match: { label: 'Matches SOZO', color: 'success' },
};

const STATUS_COLOR: Record<string, 'success' | 'error' | 'default' | 'info'> = {
  approved: 'success',
  rejected: 'error',
  superseded: 'default',
  pending: 'info',
};

const DEVELOPMENT_MODEL_LABEL: Record<DevelopmentModel, string> = {
  internal: 'In-house',
  partnered: 'Co-developed',
  partner_led: 'Partner-led',
  unknown: 'Not stated',
};

// Partner-led is warning-coloured, not error-coloured: it is a legitimate part
// of the pipeline worth seeing, but never addable as this company's own asset.
const DEVELOPMENT_MODEL_COLOR: Record<
  DevelopmentModel,
  'default' | 'info' | 'warning' | 'success'
> = {
  internal: 'success',
  partnered: 'info',
  partner_led: 'warning',
  unknown: 'default',
};

const DEVELOPMENT_MODEL_HELP: Record<DevelopmentModel, string> = {
  internal: 'The company runs development itself',
  partnered: 'The source presents development as shared',
  partner_led:
    'A named partner runs development. Shown for context; it cannot be ' +
    'added as this company’s asset.',
  unknown: 'The source does not say who runs development',
};

// The eligibility criteria, rendered in the order the extraction plan states
// them so a reviewer reading two suggestions sees failures in a stable place.
const CRITERION_LABELS: Record<string, string> = {
  named_asset: 'No explicit asset name',
  company_attributed: 'Not attributed to this company',
  preclinical_stage: 'Not preclinical',
  substantive_detail: 'Preclinical stage not stated for this asset',
  grounded_evidence: 'Evidence not grounded',
  current_program: 'Not an active program',
  missing_from_sozo: 'Already in SOZO',
};

const formatDate = (value?: string | null): string => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString();
};

interface SuggestionRowProps {
  suggestion: PipelineSuggestion;
  onApprove: (suggestion: PipelineSuggestion) => void;
  onReject: (key: string) => void;
  onReopen: (key: string) => void;
  busy: boolean;
}

const SuggestionRow: React.FC<SuggestionRowProps> = ({
  suggestion,
  onApprove,
  onReject,
  onReopen,
  busy,
}) => {
  const [expanded, setExpanded] = useState(false);
  const meta = DIFFERENCE_META[suggestion.difference_type] ?? {
    label: suggestion.difference_type,
    color: 'default' as const,
  };

  const stage = suggestion.normalized_stage
    ? suggestion.normalized_stage
    : suggestion.reported_stage
    ? `${suggestion.reported_stage} (unmapped)`
    : '—';

  // Ownership fields were added after runs had already been written, so every
  // stored record from before then lacks them. The API defaults what it can,
  // but a suggestion can still arrive without these keys — read them
  // defensively rather than trusting the declared type.
  const partnerNames = suggestion.partner_names ?? [];
  const developmentModel: DevelopmentModel =
    suggestion.development_model &&
    suggestion.development_model in DEVELOPMENT_MODEL_LABEL
      ? suggestion.development_model
      : 'unknown';

  // Fall back to the company only when the source attributed the asset to it.
  // For a partner-led program with no named owner, an em dash is the honest
  // answer — naming the company we happened to be reading would assert the
  // opposite of what the evidence says.
  const ownerLabel =
    suggestion.owner_name ||
    (suggestion.company_attributed !== false ? suggestion.company_name : null) ||
    '—';
  const evidenceByField = suggestion.evidence_by_field ?? {};
  const pipelineFields = [
    { key: 'therapeutic_name', label: 'Therapeutic', value: suggestion.therapeutic_name },
    { key: 'reported_stage', label: 'Stage as stated', value: suggestion.reported_stage },
    { key: 'normalized_stage', label: 'Normalized stage', value: suggestion.normalized_stage },
    { key: 'indication_text', label: 'Indication', value: suggestion.indication_text },
    { key: 'modality', label: 'Modality', value: suggestion.modality },
    { key: 'mechanism_of_action', label: 'Mechanism of action', value: suggestion.mechanism_of_action },
    { key: 'biological_targets', label: 'Biological target', value: suggestion.targets },
    { key: 'owner_name', label: 'Owner / primary developer', value: suggestion.owner_name },
    { key: 'development_model', label: 'Development model', value: developmentModel },
    { key: 'partner_names', label: 'Partners', value: partnerNames },
    { key: 'substantive_facts', label: 'Other source-stated facts', value: suggestion.substantive_facts },
  ];

  return (
    <>
      <TableRow hover>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight={600}>
              {suggestion.therapeutic_name}
            </Typography>
            {suggestion.addable && (
              <Tooltip title="Eligible for addition as a preclinical asset">
                <Chip
                  size="small"
                  color="success"
                  icon={<AddCircleOutlineIcon />}
                  label="Addable"
                />
              </Tooltip>
            )}
          </Stack>
          {suggestion.matched_therapeutic_name && (
            <Typography variant="caption" color="text.secondary">
              matched {suggestion.matched_therapeutic_name}
              {suggestion.sozo_stage ? ` · SOZO ${suggestion.sozo_stage}` : ''}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2">{ownerLabel}</Typography>
          {partnerNames.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              with {partnerNames.join(', ')}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <Tooltip title={DEVELOPMENT_MODEL_HELP[developmentModel]}>
            <Chip
              size="small"
              variant="outlined"
              color={DEVELOPMENT_MODEL_COLOR[developmentModel]}
              label={DEVELOPMENT_MODEL_LABEL[developmentModel]}
            />
          </Tooltip>
        </TableCell>
        <TableCell>
          <Chip size="small" color={meta.color} label={meta.label} />
        </TableCell>
        <TableCell>{stage}</TableCell>
        <TableCell>{suggestion.indication_text || '—'}</TableCell>
        <TableCell>
          <PipelineBiologicalTarget
            targets={suggestion.targets ?? []}
            stated={suggestion.biological_target_stated === true}
          />
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            variant="outlined"
            color={STATUS_COLOR[suggestion.status] ?? 'default'}
            label={suggestion.status}
          />
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => setExpanded((value) => !value)}
              endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            >
              Evidence
            </Button>
            {suggestion.status === 'pending' ? (
              <>
                {suggestion.addable && (
                  <Tooltip title="Create the canonical Therapeutic and preclinical DrugDevelopment">
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      disabled={busy}
                      onClick={() => onApprove(suggestion)}
                    >
                      Approve
                    </Button>
                  </Tooltip>
                )}
                <Button
                  size="small"
                  color="error"
                  disabled={busy}
                  onClick={() => onReject(suggestion.key)}
                >
                  Reject
                </Button>
              </>
            ) : (
              <Button
                size="small"
                disabled={busy}
                startIcon={<UndoIcon />}
                onClick={() => onReopen(suggestion.key)}
              >
                Reopen
              </Button>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell colSpan={9} sx={{ bgcolor: 'action.hover' }}>
            <Stack spacing={2} sx={{ py: 1 }}>
              {suggestion.failed_criteria.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Why this is not addable
                  </Typography>
                  <Stack spacing={0.5}>
                    {suggestion.failed_criteria.map((criterion) => (
                      <Typography key={criterion} variant="body2">
                        • {CRITERION_LABELS[criterion] ?? criterion}
                        {suggestion.eligibility_reasons[criterion]
                          ? ` — ${suggestion.eligibility_reasons[criterion]}`
                          : ''}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Pipeline data and field evidence
                </Typography>
                <PipelineFieldEvidence
                  fields={pipelineFields}
                  biologicalTargetStated={suggestion.biological_target_stated === true}
                  evidenceByField={evidenceByField}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Source evidence
                </Typography>
                {suggestion.evidence.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No verified evidence.
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {suggestion.evidence.map((item, index) => (
                      <Box key={`${item.content_id}-${index}`}>
                        <Typography
                          variant="body2"
                          sx={{ fontStyle: 'italic', mb: 0.5 }}
                        >
                          “{item.evidence_text}”
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {item.source_title || item.content_id}
                          {item.source_type ? ` · ${item.source_type}` : ''}
                          {item.page_or_slide ? ` · p/s ${item.page_or_slide}` : ''}
                          {item.published_date
                            ? ` · ${formatDate(item.published_date)}`
                            : ''}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              {suggestion.notes.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Notes
                  </Typography>
                  {suggestion.notes.map((note, index) => (
                    <Typography key={index} variant="body2">
                      • {note}
                    </Typography>
                  ))}
                </Box>
              )}

              {suggestion.reviewed_by && (
                <Typography variant="caption" color="text.secondary">
                  {suggestion.status} by {suggestion.reviewed_by}
                  {suggestion.reviewed_at
                    ? ` on ${formatDate(suggestion.reviewed_at)}`
                    : ''}
                  {suggestion.review_note ? ` — “${suggestion.review_note}”` : ''}
                </Typography>
              )}
            </Stack>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const PipelineIntelligencePage: React.FC = () => {
  const [overview, setOverview] = useState<PipelineIntelligenceOverview | null>(null);
  const [companies, setCompanies] = useState<CompanySummary[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<PipelineSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);

  // Approval confirmation. Production writes require an explicit confirm, and
  // an asset whose source stated no indication cannot be approved until the
  // reviewer supplies one — the system never invents a disease.
  const [approving, setApproving] = useState<PipelineSuggestion | null>(null);
  const [indicationInput, setIndicationInput] = useState('');

  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');

  // Ad-hoc run controls
  const [searchOptions, setSearchOptions] = useState<CompanyListItem[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [runCompany, setRunCompany] = useState<CompanyListItem | null>(null);
  const [runSource, setRunSource] = useState('all');
  const [runDays, setRunDays] = useState(180);
  const [running, setRunning] = useState(false);
  const [runMessage, setRunMessage] = useState<string | null>(null);
  // Candidate count, fetched when a company is picked. Shows what a run would
  // read before any LLM call is made, so an empty environment is obvious up
  // front rather than after a run that appears to do nothing.
  const [candidateInfo, setCandidateInfo] = useState<string | null>(null);

  const loadOverview = useCallback(async () => {
    try {
      const [overviewData, companyData] = await Promise.all([
        getPipelineIntelligenceOverview(),
        listPipelineIntelligenceCompanies(),
      ]);
      setOverview(overviewData);
      setCompanies(companyData);
    } catch (err) {
      setError('Could not load pipeline intelligence results.');
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const loadSuggestions = useCallback(
    async (companyId: string) => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCompanySuggestions(companyId, {
          status: statusFilter === 'all' ? undefined : statusFilter,
          difference_type: typeFilter === 'all' ? undefined : typeFilter,
        });
        setSuggestions(data.suggestions);
      } catch (err) {
        setError('Could not load suggestions for this company.');
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, typeFilter],
  );

  useEffect(() => {
    if (selectedCompanyId) {
      loadSuggestions(selectedCompanyId);
    }
  }, [selectedCompanyId, loadSuggestions]);

  useEffect(() => {
    let active = true;
    if (searchValue.trim().length < 2) {
      setSearchOptions([]);
      return () => {
        active = false;
      };
    }
    const timer = setTimeout(async () => {
      try {
        const result = await searchCompanies(searchValue, 20);
        if (active) setSearchOptions(result.results || []);
      } catch {
        if (active) setSearchOptions([]);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [searchValue]);

  const handleDecision = async (
    key: string,
    action: 'reject' | 'reopen',
  ): Promise<void> => {
    setBusyKey(key);
    try {
      if (action === 'reject') {
        await rejectSuggestion(key);
      } else {
        await reopenSuggestion(key);
      }
      if (selectedCompanyId) await loadSuggestions(selectedCompanyId);
      await loadOverview();
    } catch (err) {
      setError(`Could not ${action} the suggestion.`);
    } finally {
      setBusyKey(null);
    }
  };

  useEffect(() => {
    let active = true;
    if (!runCompany) {
      setCandidateInfo(null);
      return () => {
        active = false;
      };
    }
    (async () => {
      try {
        const data = await getCandidateDocuments(runCompany.id, {
          source_type: runSource,
          days_back: runDays,
        });
        if (!active) return;
        const parts = Object.entries(data.by_source)
          .map(([source, count]) => `${count} ${source.replace('_', ' ')}`)
          .join(', ');
        setCandidateInfo(
          data.total === 0
            ? 'No documents available for this company in this environment.'
            : `${data.total} document(s) available: ${parts}.`,
        );
      } catch {
        if (active) setCandidateInfo(null);
      }
    })();
    return () => {
      active = false;
    };
  }, [runCompany, runSource, runDays]);

  const handleApprove = async (): Promise<void> => {
    if (!approving) return;
    const needsIndication = !approving.indication_text;
    if (needsIndication && !indicationInput.trim()) return;

    setBusyKey(approving.key);
    try {
      const result = await approveSuggestion(approving.key, {
        indicationOverride: needsIndication ? indicationInput.trim() : undefined,
      });
      const applied = (result.applied ?? {}) as Record<string, string>;
      setRunMessage(
        `Approved. Therapeutic ${applied.therapeutic_id}, ` +
          `development ${applied.development_id}.`,
      );
      setApproving(null);
      setIndicationInput('');
      if (selectedCompanyId) await loadSuggestions(selectedCompanyId);
      await loadOverview();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ?? 'Could not approve the suggestion.',
      );
    } finally {
      setBusyKey(null);
    }
  };

  const handleRun = async (): Promise<void> => {
    if (!runCompany) return;
    setRunning(true);
    setRunMessage(null);
    setError(null);
    try {
      const result = await runPipelineIntelligencePreview(runCompany.id, {
        source_type: runSource as 'all' | 'press_release' | 'presentation',
        days_back: runDays,
        persist: true,
      });
      if (result.error) {
        setError(result.error);
      } else if (result.documents_read === 0) {
        // A run that read nothing is the most confusing outcome: it looks
        // like a broken feature rather than a company with no documents in
        // this environment. Say so plainly, and name the likely cause.
        setError(
          `No ${runSource === 'all' ? 'source documents' : runSource.replace('_', ' ') + 's'} ` +
            `found for ${runCompany.name || runCompany.id} in the last ${runDays} days ` +
            `(${result.documents_failed} unreadable). Nothing was extracted. ` +
            `If you expect documents here, check the environment selector — ` +
            `many companies only have content in production.`,
        );
      } else {
        setRunMessage(
          `Read ${result.documents_read} document(s), extracted ` +
            `${result.claims_extracted} claim(s), saved ${result.persisted}.` +
            (result.claims_extracted === 0
              ? ' No pipeline assets were claimed in those documents.'
              : ''),
        );
        setSelectedCompanyId(runCompany.id);
        await loadOverview();
      }
    } catch (err) {
      setError('The extraction run failed.');
    } finally {
      setRunning(false);
    }
  };

  const selectedCompany = useMemo(
    () => companies.find((c) => c.company_id === selectedCompanyId) ?? null,
    [companies, selectedCompanyId],
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>
        Pipeline Intelligence
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Therapeutic pipeline claims extracted from company press releases and
        presentations, reconciled against the canonical SOZO pipeline. Every
        actionable suggestion is backed by a verified source passage.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {runMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRunMessage(null)}>
          {runMessage}
        </Alert>
      )}

      {overview && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={4} flexWrap="wrap">
            <Box>
              <Typography variant="h5">{overview.total_suggestions}</Typography>
              <Typography variant="caption" color="text.secondary">
                suggestions
              </Typography>
            </Box>
            <Box>
              <Typography variant="h5">{overview.companies}</Typography>
              <Typography variant="caption" color="text.secondary">
                companies
              </Typography>
            </Box>
            <Box>
              <Typography variant="h5" color="success.main">
                {overview.addable}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                addable preclinical
              </Typography>
            </Box>
            <Box>
              <Typography variant="h5">{overview.by_status.pending ?? 0}</Typography>
              <Typography variant="caption" color="text.secondary">
                pending review
              </Typography>
            </Box>
            {overview.last_run_at && (
              <Box>
                <Typography variant="body2">
                  {formatDate(overview.last_run_at)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  last run
                </Typography>
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>
          Run a review
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <Autocomplete
            sx={{ minWidth: 280 }}
            options={searchOptions}
            getOptionLabel={(option) => option.name || option.id}
            value={runCompany}
            onChange={(_, value) => setRunCompany(value)}
            onInputChange={(_, value) => setSearchValue(value)}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Company" />
            )}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sources</InputLabel>
            <Select
              label="Sources"
              value={runSource}
              onChange={(e) => setRunSource(e.target.value)}
            >
              <MenuItem value="all">All sources</MenuItem>
              <MenuItem value="press_release">Press releases</MenuItem>
              <MenuItem value="presentation">Presentations</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="number"
            label="Days back"
            value={runDays}
            onChange={(e) => setRunDays(Number(e.target.value) || 180)}
            sx={{ width: 120 }}
          />
          <Button
            variant="contained"
            startIcon={running ? <CircularProgress size={16} /> : <PlayArrowIcon />}
            disabled={!runCompany || running}
            onClick={handleRun}
          >
            {running ? 'Running…' : 'Run'}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Reads the selected sources, extracts pipeline claims, and saves
          evidence-backed suggestions. This calls an LLM per document.
        </Typography>
        {candidateInfo && (
          <Typography
            variant="caption"
            sx={{ mt: 0.5, display: 'block' }}
            color={candidateInfo.startsWith('No documents') ? 'warning.main' : 'text.secondary'}
          >
            {candidateInfo}
          </Typography>
        )}
      </Paper>

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems="flex-start">
        <Paper sx={{ p: 2, width: { xs: '100%', lg: 360 }, flexShrink: 0 }}>
          <Typography variant="subtitle1" gutterBottom>
            Companies
          </Typography>
          {companies.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No results yet. Run a review to get started.
            </Typography>
          ) : (
            <Stack divider={<Divider />}>
              {companies.map((company) => (
                <Box
                  key={company.company_id}
                  onClick={() => setSelectedCompanyId(company.company_id)}
                  sx={{
                    py: 1,
                    px: 1,
                    cursor: 'pointer',
                    bgcolor:
                      company.company_id === selectedCompanyId
                        ? 'action.selected'
                        : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {company.company_name || company.company_id}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    {company.addable > 0 && (
                      <Chip
                        size="small"
                        color="success"
                        label={`${company.addable} addable`}
                      />
                    )}
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${company.total_suggestions} total`}
                    />
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Paper>

        <Paper sx={{ p: 2, flexGrow: 1, width: '100%' }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="subtitle1">
              {selectedCompany
                ? selectedCompany.company_name || selectedCompany.company_id
                : 'Select a company'}
            </Typography>
            <Stack direction="row" spacing={1}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All statuses</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Difference</InputLabel>
                <Select
                  label="Difference"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="all">All differences</MenuItem>
                  {Object.entries(DIFFERENCE_META).map(([value, meta]) => (
                    <MenuItem key={value} value={value}>
                      {meta.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !selectedCompanyId ? (
            <Typography variant="body2" color="text.secondary">
              Choose a company to review its suggestions.
            </Typography>
          ) : suggestions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No suggestions match the current filters.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Asset</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Developed by</TableCell>
                    <TableCell>Difference</TableCell>
                    <TableCell>Stage</TableCell>
                    <TableCell>Indication</TableCell>
                    <TableCell>Biological target</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suggestions.map((suggestion) => (
                    <SuggestionRow
                      key={suggestion.key}
                      suggestion={suggestion}
                      busy={busyKey === suggestion.key}
                      onApprove={(item) => {
                        setApproving(item);
                        setIndicationInput('');
                      }}
                      onReject={(key) => handleDecision(key, 'reject')}
                      onReopen={(key) => handleDecision(key, 'reopen')}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <Dialog
        open={approving !== null}
        onClose={() => setApproving(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve {approving?.therapeutic_name}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This creates a canonical Therapeutic and a preclinical
            DrugDevelopment, and links every source document as evidence. The
            operation is idempotent — approving twice will not duplicate them.
          </DialogContentText>

          {approving && !approving.indication_text && (
            <>
              <DialogContentText sx={{ mb: 1 }} color="warning.main">
                The source stated no indication. Enter one, or use “Unknown” to
                apply the established convention. Nothing will be inferred.
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Indication"
                value={indicationInput}
                onChange={(e) => setIndicationInput(e.target.value)}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproving(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={
              busyKey !== null ||
              (approving !== null &&
                !approving.indication_text &&
                !indicationInput.trim())
            }
            onClick={handleApprove}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PipelineIntelligencePage;
